import { useEffect, useMemo, useRef, useState } from 'react';
import { LevelProgressState } from '../types';
import { getLevelProgress } from '../utils/experience';
import { humanizeNumber } from '../utils/format';

interface Props {
  totalExp: number;
  animateFrom?: number | null;
  scaleOnReveal?: boolean;
  gained?: number | null;
}

/**
 * Displays the player's current level and animated progress toward the next one.
 */
export function ExperienceProgress({
  totalExp,
  animateFrom,
  scaleOnReveal,
  gained,
}: Props) {
  const initial = animateFrom ?? totalExp;
  const [displayExp, setDisplayExp] = useState(initial);
  const [levelUpFlash, setLevelUpFlash] = useState(false);
  const [scaleFlash, setScaleFlash] = useState(false);
  const levelRef = useRef<LevelProgressState>(getLevelProgress(initial));
  const rafRef = useRef<number | null>(null);
  const timersRef = useRef<number[]>([]);

  const progress = useMemo(() => getLevelProgress(displayExp), [displayExp]);

  const clearTimers = () => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  };

  useEffect(() => {
    if (animateFrom === undefined || animateFrom === null || animateFrom === totalExp) {
      setDisplayExp(totalExp);
      levelRef.current = getLevelProgress(totalExp);
      if (scaleOnReveal) {
        setScaleFlash(true);
        const timer = window.setTimeout(() => setScaleFlash(false), 700);
        timersRef.current.push(timer);
        return () => clearTimers();
      }
      return;
    }

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
    const baseDuration = 900;
    const accelFactor = 0.85;
    const minDuration = 320;

    type Segment = { from: number; to: number; duration: number };
    const segments: Segment[] = [];
    let cursor = animateFrom;
    let levelUps = 0;

    // Build per-level segments so the bar resets at each level up.
    while (cursor < totalExp && levelUps < 50) {
      const current = getLevelProgress(cursor);
      const expToLevelEnd = current.expForNext - current.expIntoLevel;
      const nextBreakpoint = cursor + expToLevelEnd;
      const target = Math.min(totalExp, nextBreakpoint);
      const duration = Math.max(minDuration, Math.round(baseDuration * Math.pow(accelFactor, levelUps)));
      segments.push({ from: cursor, to: target, duration });
      if (target >= nextBreakpoint) {
        levelUps += 1;
      }
      cursor = target;
    }

    let cancelled = false;

    const runSegment = (index: number) => {
      if (cancelled || index >= segments.length) {
        setDisplayExp(totalExp);
        return;
      }
      const { from, to, duration } = segments[index];
      const start = performance.now();

      const step = (now: number) => {
        if (cancelled) return;
        const elapsed = now - start;
        const ratio = Math.min(1, elapsed / duration);
        const eased = easeOut(ratio);
        const nextValue = Math.round(from + (to - from) * eased);
        setDisplayExp(nextValue);

        if (ratio < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          rafRef.current = null;
          // Small delay before next level to allow reset to show.
          const delay = window.setTimeout(() => runSegment(index + 1), 80);
          timersRef.current.push(delay);
        }
      };

      rafRef.current = requestAnimationFrame(step);
    };

    runSegment(0);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      cancelled = true;
      clearTimers();
    };
  }, [animateFrom, totalExp]);

  useEffect(() => {
    if (progress.level > levelRef.current.level) {
      setLevelUpFlash(true);
      levelRef.current = progress;
      const timerA = window.setTimeout(() => setLevelUpFlash(false), 900);
      timersRef.current.push(timerA);
      return () => clearTimers();
    }
    levelRef.current = progress;
    return undefined;
  }, [progress]);

  useEffect(() => {
    if (!scaleOnReveal) return undefined;
    setScaleFlash(true);
    const timer = window.setTimeout(() => setScaleFlash(false), 700);
    return () => window.clearTimeout(timer);
  }, [scaleOnReveal, totalExp]);

  return (
    <div className={`xp-card minimal${levelUpFlash ? ' level-up' : ''}${scaleFlash ? ' scale-up' : ''}`}>
      <div className="xp-card-row">
        <h3 className="xp-level">Level {progress.level}</h3>
        <span className="xp-total">
          Total XP {humanizeNumber(displayExp)}
          {gained && gained > 0 ? ` (+${humanizeNumber(gained)})` : ''}
        </span>
      </div>
      <div
        className="xp-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuenow={Math.round(progress.progress * 100)}
        aria-valuemax={100}
        aria-label="Experience progress"
      >
        <div className="xp-progress-fill" style={{ width: `${progress.progress * 100}%` }} />
        <div className="xp-progress-glow" style={{ width: `${progress.progress * 100}%` }} />
      </div>
      <div className="xp-meta">
        <span>{humanizeNumber(progress.expIntoLevel)} / {humanizeNumber(progress.expForNext)} XP</span>
        <span>{Math.round(progress.progress * 100)}%</span>
      </div>
    </div>
  );
}
